import React, { Fragment, Component } from 'react';
import { createRefetchContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { injectIntl, defineMessages } from 'react-intl';
import classNames from 'classnames';

import Resizer from 'react-image-file-resizer';

import DateFnsUtils from '@date-io/date-fns';
import CircularProgress from '@material-ui/core/CircularProgress';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';

import deLocale from "date-fns/locale/de"
import enLocale from "date-fns/locale/en-US";
import frLocale from "date-fns/locale/fr";
import nlLocale from "date-fns/locale/nl";
import itLocale from "date-fns/locale/it";
import esLocale from "date-fns/locale/es";
import zhLocale from "date-fns/locale/zh-CN";
import ptLocale from "date-fns/locale/pt";

import withRequestHandler from '../../hoc/withRequestHandler';

import SearchInstructionsDialog from '../dialogs/SearchInstructionsDialog';
import { FaceRecognitionUpload } from '../face-recognition-upload';

import { UploadFacePhotoMutation } from '../../../mutations';

import { FormattedMessageWrappedInSpan } from '../../misc';
import { trackEvent } from '../../../utils/ga-tracking';
import { sendError } from '../../../appsignal';

import {
  FACTOR_EMAIL,
  FACTOR_DATE_OF_BIRTH,
  FACTOR_YEAR_OF_BIRTH,
} from '../../../utils/variables';
import { getCustomerKey, setToLocalStorage, getFromLocalStorage, removeFromLocalStorage} from '../../../utils/browser-storage';

import 'whatwg-fetch';

const localeMap = {
  de: deLocale,
  en: enLocale,
  fr: frLocale,
  nl: nlLocale,
  it: itLocale,
  es: esLocale,
  zh: zhLocale,
  pt: ptLocale,
};

const ENTER_KEY = 'Enter';
const SECOND_FACTOR = 'secondFactor';
const START_NUMBER = 'startNumber';

class StartNumberSearch extends Component {
  constructor(props) {
    super(props);

    //only show instructions if we start with an empty search field / no face record and when warnText is there for the event
    this.state = {
      showSpinner: false,
      recognitionPhoto: null,
      searchInstructionsOpen: false,
      secondFactorInputIsFocused: false,
      shouldShowSearchInstructions: !this.props.startNumber && this.props.info.warnText,
      shouldShowSearchByFaceInstructions: this.props.info.searchByFace && this.props.info.warnText,
      showStartNumberError: false,
      showSecondFactorError: false,
      tempInputValue: '',
      locale: getFromLocalStorage('language'),
    };

    this.handleBlur = this.handleBlur.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleSearchClick = this.handleSearchClick.bind(this);
    this.handleDatePickerDismiss = this.handleDatePickerDismiss.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);
    this.handleRecognitionPhotoChange = this.handleRecognitionPhotoChange.bind(this);
  }

  componentDidMount() {
    const { shouldShowSearchByFaceInstructions } = this.state;
    if (this.startNumberInputRef) {
      // This will not be true if the face recognition search is available. In that case
      // no start number input field will be rendered.
      !this.props.startNumber && this.startNumberInputRef.focus();
    } else if (shouldShowSearchByFaceInstructions && getFromLocalStorage('base64-photo') === null && !this.props.showSearchInstructionsDialog) {
      this.showSearchByFaceInstructions();
    }
  }

  handleInputChange(event) {
    const { info, onInputChange } = this.props;
    this.setState({ showStartNumberError: false, showSecondFactorError: false });

    // trim the value
    const trimmedValue = event.target.value.trim();

    if (event.target.name === START_NUMBER) {
      onInputChange(START_NUMBER, trimmedValue);

      // If a second factor exists
      // Update second factor input
      if (info.secondFactorType) {
        const ck = getCustomerKey(info.sgId, trimmedValue);
        if (ck) {
          onInputChange(SECOND_FACTOR, window.atob(ck));
        } else {
          onInputChange(SECOND_FACTOR, '');
        }
      }
    }

    if (event.target.name === SECOND_FACTOR) {
      // Can only be true if the event has a 2nd factor
      if (info.secondFactorType.type === FACTOR_YEAR_OF_BIRTH) {
        // If the the second factor is the year of birth, only allow numbers
        // isNaN(...) determines if a value does not convert to a number
        if (!isNaN(trimmedValue)) {
          onInputChange(SECOND_FACTOR, trimmedValue);
        }
      } else {
        onInputChange(SECOND_FACTOR, trimmedValue);
      }
    }
  }

  handleKeyPress(event) {
    if (event.key === ENTER_KEY) {
      this.handleSearchClick();
    }
  }

  handleFocus(event) {
    if (this.state.shouldShowSearchInstructions) {
      this.showSearchInstructions();
    }

    this.setState({
      secondFactorInputIsFocused: event.target.name === SECOND_FACTOR,
    });
  }

  handleBlur(event) {
    if (event.target.name === SECOND_FACTOR) {
      this.setState({ secondFactorInputIsFocused: false });
    }
    if (event.target.name === START_NUMBER) {
      // If the start number input lost focus and has no value (input is empty), insert
      // the start number from the url
      if (this.props.startNumber === '') {
        const { startnumber } = this.props.match.params;
        if (startnumber) {
          this.handleInputChange(event, startnumber);
        }
      }
    }
  }

  handleDatePickerDismiss() {
    // Only called from DatePicker if a second factor is necessary
    this.setState({ secondFactorInputIsFocused: false });
  }

  handleDateChange(date) {
    this.setState({ showStartNumberError: false, showSecondFactorError: false });
    const formattedDate = this._formatDateToString(date);
    this.props.onInputChange(SECOND_FACTOR, formattedDate);
  }

  handleSearchClick() {
    const { info, relay, startNumber, secondFactor, onSearchTrigger } = this.props;
    const { recognitionPhoto } = this.state;

    if (info.searchByFace && recognitionPhoto === null) {
      document.getElementById("faceRecoInput").click();
      return
    }

    // Show spinner after search button click
    this.setState({ showSpinner: true });

    // Using face recognition
    if (recognitionPhoto !== null) {
      this._encodeFile(recognitionPhoto)
        .then(function(encodedPhoto) {
          setToLocalStorage('base64-photo', encodedPhoto)
        }).catch(error => {
          sendError(error);
        });
      this.props.onRequestStart();
      UploadFacePhotoMutation.commit(
        relay.environment,
        recognitionPhoto,
        info.sgId,
        (error, _startNumber) => {
          // Hide spinner before navigating to search results
          this.setState({ showSpinner: false });

          if (!error) {
            this.props.onInputChange(START_NUMBER, _startNumber);
            this._navigateToSearchPath();
            this.props.onRequestStop();
          } else {
            this.props.onRequestError(error);
          }
        }
      );
      return;
    }

    // hide spinner after request is done
    this.setState({ showSpinner: false });
    
    if (startNumber === '') {
      this.setState({ showStartNumberError: true });
      this.startNumberInputRef.focus();
      return;
    }
    // If a second factor is necessary
    if (info.secondFactorType) {
      // If the start number has already been searched for, the value of the 'secondFactor'
      // can be ignored
      const customerKey = getCustomerKey(info.sgId, startNumber);
      if (customerKey) {
        // Whenever a new search is triggered, the date should be displayed that matches
        // the start number.
        this.props.onInputChange(SECOND_FACTOR, window.atob(customerKey));
        this._navigateToSearchPath(customerKey);
        onSearchTrigger && onSearchTrigger();
        return;
      }

      if (secondFactor === '') {
        this.setState({ showSecondFactorError: true });
        if (info.secondFactorType.type !== FACTOR_DATE_OF_BIRTH) {
          this.secondFactorInputRef.focus();
        }
        return;
      }

      const encodedSecondFactor = window.btoa(secondFactor);
      this._navigateToSearchPath(encodedSecondFactor);
      onSearchTrigger && onSearchTrigger();

      // Refetch the graphql query to get the 'secondFactorEventSalt'
      // INFO: Not necessary at the moment
      // relay.refetch({ individualPart: startNumber }, null, () => {
      //   // If this callback is beeing called, the new props are available
      //   // Encode the second factor in base64
      // });
    } else {
      this._navigateToSearchPath();
      onSearchTrigger && onSearchTrigger();
    }

    trackEvent('Event', 'Search', 'Search for start number button');
  }

  handleRecognitionPhotoChange(event) {
    const selectedPhoto = event.target.files[0];
    // 'selectedPhoto' can be undefined if no file has been selected in the file manager
    if (selectedPhoto) {
      Resizer.imageFileResizer(selectedPhoto, 800, 800, 'JPEG', 70, 0, uri => {
          this._decodeToFile(uri)
            .then(function(photoFile) {
              this.setState({ recognitionPhoto: photoFile });
              this.props.onInputChange(START_NUMBER, '');
            }.bind(this)).catch(error => {
              sendError(error);
            });
        },
      );
    }
  }

  showSearchInstructions = () => {
    this.startNumberInputRef.blur();
    this.setState({
      searchInstructionsOpen: true,
    });
  };

  showSearchByFaceInstructions = () => {
    this.setState({
      searchInstructionsOpen: true,
    });
  };

  hideSearchInstructions = () => {
    if (this.props.info.searchByFace) {
      this.setState({
        searchInstructionsOpen: false,
      });
    } else {
      this.setState(
        {
          searchInstructionsOpen: false,
          shouldShowSearchInstructions: false,
        },
        () => {
          this.startNumberInputRef.focus();
          this.props.onInputChange(this.state.tempInputValue);
        }
      );
    }
  };

  _navigateToSearchPath(customerKey) {
    const { history, match, info, startNumber } = this.props;
    let destinationUrl = '';
    if (startNumber === undefined) {
      destinationUrl = `/event/${info.sgId}/search/no-photos-found`;
    } else {
      destinationUrl = `/event/${info.sgId}/search/${startNumber}`;
    }

    if (customerKey) {
      destinationUrl = `${destinationUrl}/${customerKey}`;
    }

    switch (match.path) {
      case '/:language/event/:id':
        history.push(destinationUrl);
        break;
      default:
        history.replace(destinationUrl);
        break;
    }
  }

  _getSecondFactorInputHintText(type) {
    let translationId = null;
    switch (type) {
      case FACTOR_DATE_OF_BIRTH:
        translationId = translations.secondFactorBirthDateHintText;
        break;
      case FACTOR_EMAIL:
        translationId = translations.secondFactorEmailHintText;
        break;
      case FACTOR_YEAR_OF_BIRTH:
        translationId = translations.secondFactorYearOfBirthHintText;
        break;
      default:
        return null;
    }
    return this.props.intl.formatMessage(translationId);
  }

  _renderSecondFactorTitle(type) {
    switch (type) {
      case FACTOR_DATE_OF_BIRTH:
        return (
          <FormattedMessageWrappedInSpan
            id="startNumberSearch.secondFactor.birthDateType.floatingLabelText"
            defaultMessage="Select your date of birth"
          />
        );
      case FACTOR_EMAIL:
        return (
          <FormattedMessageWrappedInSpan
            id="startNumberSearch.secondFactor.emailType.floatingLabelText"
            defaultMessage="Enter your email"
          />
        );
      case FACTOR_YEAR_OF_BIRTH:
        return (
          <FormattedMessageWrappedInSpan
            id="startNumberSearch.secondFactor.yearOfBirthType.floatingLabelText"
            defaultMessage="Enter your year of birth"
          />
        );
      default:
        return null;
    }
  }

  _formatDateToString(date) {
    // 01, 02, 03, ... 29, 30, 31
    const dd = (date.getDate() < 10 ? '0' : '') + date.getDate();
    // 01, 02, 03, ... 10, 11, 12
    const mm = (date.getMonth() + 1 < 10 ? '0' : '') + (date.getMonth() + 1);
    // 1970, 1971, ... 2015, 2016, ...
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }

  _formatStringToDate(s) {
    if (!s) {
      return null;
    }
    const splitted = s.split('-');
    const dd = Number(splitted[0]);
    const mm = Number(splitted[1]) - 1;
    const yyyy = Number(splitted[2]);
    return new Date(yyyy, mm, dd);
  }

  // Encode a file as base64
  _encodeFile(file) {
    return new Promise(function(resolve, reject) {
      const reader = new FileReader();
      reader.onloadend = function() {
        resolve(reader.result);
      };
      reader.readAsDataURL(file);
    });
  }

  //return a promise that resolves with a File instance
  _decodeToFile(b64Data, sliceSize=1024) {

    return new Promise(function(resolve, reject) {
      const byteCharacters = atob(b64Data.split(',')[1]);
      const byteArrays = [];
    
      for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
    
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
    
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }
    
      const blob = new Blob(byteArrays, { type: 'image/*' });
      resolve(blob);
    });
  }

  setFacePhotoFromCache(startNumber) {
    const encodedPhoto = getFromLocalStorage('base64-photo');
    if (startNumber !== '' && encodedPhoto !== null) {
      this._decodeToFile(encodedPhoto)
        .then(function(photoFile) {
          this.setState({ recognitionPhoto: photoFile });
        }.bind(this)).catch(error => {
          sendError(error);
        });
    } else {
      removeFromLocalStorage('base64-photo');
      return null;
    }
  }

  render() {
    const { intl, info, startNumber, secondFactor } = this.props;
    const {
      showSpinner,
      recognitionPhoto,
      secondFactorInputIsFocused,
      showStartNumberError,
      showSecondFactorError,
    } = this.state;

    // let language = getFromLocalStorage('language');
    // language !== null ? this.setState({ locale: language }) : this.setState({ locale: 'en' })

    return (
      <div className="col-xs-16 col-sm-6">
        <div className="startnumber-search-container">
          {info.searchByFace && (
            <FaceRecognitionUpload
              imgSrc={
                recognitionPhoto !== null && recognitionPhoto !== undefined ? URL.createObjectURL(recognitionPhoto) : this.setFacePhotoFromCache(startNumber)
              }
              onChange={this.handleRecognitionPhotoChange}
            />
          )}
          {!info.searchByFace && (
            <Fragment>
              <div
                className={classNames('startnumber-search-label', {
                  error: showStartNumberError,
                })}
              >
                <FormattedMessageWrappedInSpan
                  id="startNumberSearch.floatingLabelText"
                  defaultMessage="Enter your start number"
                />
              </div>
              <div
                className={classNames('startnumber-search-textfield', {
                  'input-edit-icon': startNumber === '',
                })}
              >
                <div>
                  <input
                    autoComplete="off"
                    name={START_NUMBER}
                    onChange={this.handleInputChange}
                    onBlur={this.handleBlur}
                    onKeyPress={this.handleKeyPress}
                    onFocus={this.handleFocus}
                    placeholder="0000"
                    ref={input => (this.startNumberInputRef = input)}
                    type="text"
                    value={startNumber}
                  />
                </div>
              </div>
            </Fragment>
          )}
          {info.secondFactorType && (
            <Fragment>
              <div
                className={classNames('startnumber-search-label', {
                  error: showSecondFactorError,
                })}
              >
                {this._renderSecondFactorTitle(info.secondFactorType.type)}
              </div>
              {info.secondFactorType.type === FACTOR_DATE_OF_BIRTH && (
                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={localeMap[this.state.locale]}>
                  <DatePicker
                    autoOk={true}
                    disableFuture
                    cancelLabel={intl.formatMessage(translations.datePickerCancelButton)}
                    classes={{
                      root: classNames('startnumber-search-textfield', 'second-factor', {
                        focused: secondFactorInputIsFocused,
                        'input-edit-icon': secondFactor === '',
                      }),
                    }}
                    format="dd-MM-yyyy"
                    views={["year", "month", "date"]}
                    fullWidth={true}
                    InputProps={{disableUnderline: true}}
                    minDate={new Date(1900, 0, 1)}
                    maxDate={new Date()}
                    name={SECOND_FACTOR}
                    okLabel={intl.formatMessage(translations.datePickerAcceptButton)}
                    onClose={this.handleDatePickerDismiss}
                    onChange={this.handleDateChange}
                    onFocus={this.handleFocus}
                    openTo="year"
                    placeholder={this._getSecondFactorInputHintText(FACTOR_DATE_OF_BIRTH)}
                    value={this._formatStringToDate(secondFactor)}
                  />
                </MuiPickersUtilsProvider>
              )}
              {info.secondFactorType.type !== FACTOR_DATE_OF_BIRTH && (
		<div
                  className={classNames('startnumber-search-textfield', 'second-factor', {
                    focused: secondFactorInputIsFocused,
                    'input-edit-icon': secondFactor === '',
                  })}
                >
                  <div>
                    <input
                      autoComplete="off"
                      maxLength={
                        info.secondFactorType.type === FACTOR_YEAR_OF_BIRTH
                          ? 4
                          : undefined
                      }
                      name={SECOND_FACTOR}
                      onBlur={this.handleBlur}
                      onChange={this.handleInputChange}
                      onKeyPress={this.handleKeyPress}
                      onFocus={this.handleFocus}
                      placeholder={this._getSecondFactorInputHintText(
                        info.secondFactorType.type
                      )}
                      ref={input => (this.secondFactorInputRef = input)}
                      value={secondFactor}
                    />
                  </div>
                </div>
              )}
            </Fragment>
          )}
          {showSpinner && (
            <div className="startnumber-search-spinner container-full-width center-xs">
              <CircularProgress />
            </div>
          )}
          {!showSpinner && (
            <button
              className={classNames('primary', 'fullwidth')}
              onClick={this.handleSearchClick}
            >
              {info.searchByFace &&
                <>
                  <FormattedMessageWrappedInSpan
                    id="FaceRecognitionUpload.searchButton"
                    defaultMessage="Search with selfie"
                  />
                </>
              }
              {!info.searchByFace &&
                <>
                  <FormattedMessageWrappedInSpan
                    id="startNumberSearch.searchButton"
                    defaultMessage="Search"
                  />
                </>
              }
            </button>
          )}
        </div>
        <div className="startnumber-search-hint">
          {info.hostResultUrl && (
            <a href={info.hostResultUrl} target="_blank">
              <FormattedMessageWrappedInSpan
                id="startNumberSearch.forgotStartnumberHint"
                defaultMessage="Forgot your start number?"
              />
            </a>
          )}
        </div>
        <SearchInstructionsDialog
          open={this.state.searchInstructionsOpen}
          message={info.warnText}
          onAccept={this.hideSearchInstructions}
        />
      </div>
    );
  }
}

const translations = defineMessages({
  secondFactorEmailHintText: {
    id: 'startNumberSearch.secondFactor.emailType.hintText',
    defaultMessage: 'example@domain.com',
  },
  secondFactorBirthDateHintText: {
    id: 'startNumberSearch.secondFactor.birthDateType.hintText',
    defaultMessage: 'DD-MM-YYYY',
  },
  secondFactorYearOfBirthHintText: {
    id: 'startNumberSearch.secondFactor.yearOfBirthType.hintText',
    defaultMessage: 'YYYY',
  },
  datePickerAcceptButton: {
    id: 'startNumberSearch.secondFactor.datePicker.acceptButton',
    defaultMessage: 'Ok',
  },
  datePickerCancelButton: {
    id: 'startNumberSearch.secondFactor.datePicker.cancelButton',
    defaultMessage: 'Cancel',
  },
});

StartNumberSearch.propTypes = {
  history: PropTypes.object,
  info: PropTypes.object,
  intl: PropTypes.object,
  match: PropTypes.object,
  onInputChange: PropTypes.func,
  onSearchTrigger: PropTypes.func,
  relay: PropTypes.object,
  startNumber: PropTypes.string,
  secondFactor: PropTypes.string,
  showSearchInstructionsDialog: PropTypes.bool,
  onRequestStart: PropTypes.func,
  onRequestStop: PropTypes.func,
  onRequestError: PropTypes.func,
};

export { StartNumberSearch };

export default createRefetchContainer(
  withRouter(withRequestHandler(injectIntl(StartNumberSearch))),
  {
    info: graphql`
      fragment StartNumberSearch_info on Event {
        id
        searchByFace
        secondFactorEventSalt(individualPart: $individualPart)
        secondFactorType {
          type
        }
        sgId
        hostResultUrl
        warnText
      }
    `,
  },
  graphql`
    query StartNumberSearchRefetchQuery($eventId: ID!, $individualPart: String) {
      viewer {
        event(id: $eventId) {
          secondFactorEventSalt(individualPart: $individualPart)
          ...StartNumberSearch_info
        }
      }
    }
  `
);
